import { useMemo, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { DataTableFiltersV2, useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useDataTable } from '@/hooks/use-data-table';
import { useModal } from '@/hooks/use-modal';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { BillErrorModal } from '@/modules/finance/bills/components/modals/bill-error-modal';
import { BillInProgressModal } from '@/modules/finance/bills/components/modals/bill-in-progress-modal';
import { getBillingFilterConfig } from '@/modules/finance/bills/constants/billing-table-filters-config';
import {
  BILLS_FILTER_KEY_BILLING_PERIOD,
  BILLS_TABLE_FILTER_KEYS,
  BILLS_TABLE_FILTERS_STORAGE_KEY,
} from '@/modules/finance/bills/constants/bills-filter-keys';
import { hashItemsForStaleness, useFinanceListRefresh } from '@/modules/finance/common';
import { QuickBooksNotConnectedMessage } from '@/modules/finance/common/components/quickbooks-not-connected-message';
import { useQuickBooksConnection } from '@/modules/finance/common/hooks/use-quickbooks-connection';
import { FcmNotificationType } from '@/modules/notifications';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';
import { AuthenticatedRoutes } from '@/router/routes';

import { getBills } from '../api';
import { BillingTable, DeleteBillModal } from '../components';
import { SEARCH_FILTER_MIN_CHARACTERS } from '../constants';
import { getBillingTableColumns } from '../constants/billing-table-columns';
import { BILLS_QUERY_KEY, mapBillsFilters, useBills, useRetryBill } from '../hooks';
import { BillListItem, BillStatus } from '../types';

/**
 * Structural hash for staleness detection on the list view. Status flipping
 * (e.g. in-progress → open) and the assigned bill number are the only server
 * changes a list refresh needs to surface.
 */
const hashBillListItems = (items: BillListItem[] | undefined): string =>
  hashItemsForStaleness(items, item => ({ status: item.status, number: item.number }));

export const BillsPage = () => {
  const { t } = useTranslation(['authenticated.bills', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { isQuickBooksConnected, isLoading: isQuickBooksLoading } = useQuickBooksConnection();

  const { hasPermission } = usePermissions();
  const isGenerateBillsEnabled = hasPermission(OpsiumPolicy.BillsGenerate);
  const isDeleteBillsEnabled = hasPermission(OpsiumPolicy.BillsDelete);

  const [deleteBill, setDeleteBill] = useState<BillListItem | null>(null);
  const [errorBill, setErrorBill] = useState<BillListItem | null>(null);
  const [inProgressBill, setInProgressBill] = useState<BillListItem | null>(null);
  const {
    open: isErrorInfoModalOpen,
    onOpen: openErroInfoModal,
    onClose: closeErrorInfoModal,
  } = useModal();
  const {
    open: isInProgressInfoModalOpen,
    onOpen: openInProgressInfoModal,
    onClose: closeInProgressInfoModal,
  } = useModal();

  const resetDeleteBill = () => setDeleteBill(null);

  // Retry mutation
  const retryMutation = useRetryBill({
    onSuccess: () => {
      setErrorBill(null);
      closeErrorInfoModal();
    },
  });

  const columns = useMemo(
    () =>
      getBillingTableColumns({
        onDeleteClick: isDeleteBillsEnabled ? setDeleteBill : undefined,
        onErrorInfo: () => {
          openErroInfoModal();
        },
        onErrorBillSelect: setErrorBill,
        onInProgressInfo: bill => {
          setInProgressBill(bill);
          openInProgressInfoModal();
        },
        onRetry: (billId: number) => {
          retryMutation.mutate(billId);
        },
        isRetrying: (billId: number) => {
          return retryMutation.isPending && errorBill?.id === billId;
        },
      }),
    [
      setDeleteBill,
      openErroInfoModal,
      openInProgressInfoModal,
      isDeleteBillsEnabled,
      retryMutation,
      errorBill,
    ]
  );

  const defaultBillingPeriod = useMemo(
    () => serializeDateRange(getDateRangeFromPreset(DateRangePreset.LastMonth)),
    []
  );

  usePersistedTableFilters({
    storageKey: BILLS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: BILLS_TABLE_FILTER_KEYS,
    defaultFilters: { [BILLS_FILTER_KEY_BILLING_PERIOD]: defaultBillingPeriod },
  });

  const [searchParams] = useSearchParams();
  const { data: customAttributes } = useCustomAttributes();
  const filterConfig = useMemo(() => getBillingFilterConfig(customAttributes), [customAttributes]);
  const filterState = useTableFilters(filterConfig);
  const appliedFilters = filterState.activeFilters;

  // Billing period is a standalone date range picker (not a dropdown filter),
  // but its value is persisted in the same URL key via the shared filter state.
  // Read directly from searchParams so preset detection reacts to URL changes.
  // When the param is absent the range is null (reset state) — the backend then
  // returns all bills regardless of date. The LastMonth default is seeded into
  // the URL on first visit by usePersistedTableFilters, not here.
  const { dates, selectedPreset } = useMemo(() => {
    const paramValue = searchParams.get(BILLS_FILTER_KEY_BILLING_PERIOD);
    const parsed = paramValue ? parseDateRange(paramValue) : null;
    return { dates: parsed, selectedPreset: parsed ? detectPresetFromDates(parsed) : null };
  }, [searchParams]);

  const handleBillingPeriodChange = (newDates: DateRangeV2) => {
    filterState.setFilter(BILLS_FILTER_KEY_BILLING_PERIOD, serializeDateRange(newDates));
  };

  const handleBillingPeriodPreset = (preset: DateRangePreset) => {
    handleBillingPeriodChange(getDateRangeFromPreset(preset));
  };

  // Reset clears the date filter entirely (loads all bills) rather than
  // defaulting to today — see DateRangePickerV2.handleReset.
  const handleBillingPeriodReset = () => {
    filterState.clearFilter(BILLS_FILTER_KEY_BILLING_PERIOD);
  };

  const filtersWithBillingPeriod = useMemo(() => {
    const billingPeriod = searchParams.get(BILLS_FILTER_KEY_BILLING_PERIOD);
    return billingPeriod
      ? { ...appliedFilters, [BILLS_FILTER_KEY_BILLING_PERIOD]: billingPeriod }
      : appliedFilters;
  }, [searchParams, appliedFilters]);

  const { data, hasNextPage, isLoading, isFetching, isFetchingNextPage, fetchNextPage } = useBills({
    filters: filtersWithBillingPeriod,
  });

  // Unified list refresh: FCM push when enabled, long-polling fallback otherwise.
  // FCM is disabled by default while backend delivery is unreliable
  // (OPS-1560 / OPS-1449) — see useFinanceListRefresh / useFcmForFinanceLists.
  const hasInProgressBill = (data?.items ?? []).some(bill => bill.status === BillStatus.InProgress);
  const { hasPendingRefresh, refresh } = useFinanceListRefresh<BillListItem>({
    notificationType: FcmNotificationType.BILLS_READY,
    queryKey: [BILLS_QUERY_KEY],
    filters: filtersWithBillingPeriod,
    items: data?.items,
    isFetching,
    hasInProgressItem: hasInProgressBill,
    fetchAll: pageSize =>
      getBills({ filters: mapBillsFilters(filtersWithBillingPeriod), pageSize }),
    hashItems: hashBillListItems,
  });

  const hasAppliedSearch = Boolean(
    appliedFilters.searchString &&
      typeof appliedFilters.searchString === 'string' &&
      appliedFilters.searchString.length >= SEARCH_FILTER_MIN_CHARACTERS
  );

  // Temp varaible for counter
  const billingCount = data?.pagination.total ?? 0;

  const hasNonBillingPeriodFiltersApplied = Object.entries(appliedFilters).some(
    ([filterKey, filterValue]) =>
      filterKey !== BILLS_FILTER_KEY_BILLING_PERIOD && Boolean(filterValue)
  );

  const { table, saveColumnSizing } = useDataTable<BillListItem>({
    data: data?.items ?? [],
    columns,
    getRowId: row => row.id.toString(),
    tableId: TableIds.BILLS,
  });

  const handleRowClick = (bill: BillListItem) => {
    if (
      bill.status !== BillStatus.Open &&
      bill.status !== BillStatus.Overdue &&
      bill.status !== BillStatus.Error
    )
      return;

    navigate(AuthenticatedRoutes.BillDetails.replace(':billId', bill.id.toString()), {
      state: { billsSearch: location.search },
    });
  };

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        isGenerateBillsEnabled && (
          <Button
            size="sm"
            onClick={() => navigate(AuthenticatedRoutes.GenerateBills)}
            disabled={!isQuickBooksConnected}
          >
            <CirclePlus />
            {t('generateBills')}
          </Button>
        )
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
        {!isQuickBooksLoading && !isQuickBooksConnected && <QuickBooksNotConnectedMessage />}
        {hasPendingRefresh && (
          <RefreshData
            onRefresh={refresh}
            isPending={isLoading}
            dataUpdatedAt={null}
            description={t('refreshBanner.description')}
            buttonTitle={t('refreshBanner.buttonTitle')}
            containerClassName="mb-4"
          />
        )}
        <div className="flex items-start justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePickerV2
              dates={dates}
              onChange={handleBillingPeriodChange}
              selectedPreset={selectedPreset}
              onPresetSelect={handleBillingPeriodPreset}
              onReset={handleBillingPeriodReset}
              defaultPrefix={t('authenticated.bills:filters.billingPeriod')}
            />
            <DataTableFiltersV2
              filtersConfig={filterConfig}
              filterState={filterState}
              tableId={TableIds.BILLS}
              className="flex flex-wrap gap-2"
            />
          </div>
          <div className="mt-1">
            <DataTableColumnsSelector table={table} />
          </div>
        </div>
        {/* Total count */}
        <p className="my-4 shrink-0 text-xs text-muted-foreground">
          {t(`common.billsCounter.${hasAppliedSearch ? 'withSearch' : 'default'}`, {
            count: billingCount,
          })}
        </p>
        <BillingTable
          table={table}
          onRowClick={handleRowClick}
          isLoading={isLoading || isFetchingNextPage}
          hasMore={hasNextPage}
          totalCount={billingCount}
          hasNonBillingPeriodFiltersApplied={hasNonBillingPeriodFiltersApplied}
          onLoadMore={fetchNextPage}
          stickyHeader
          fixedHeight
          tableId={TableIds.BILLS}
          onResizeEnd={saveColumnSizing}
        />
        <DeleteBillModal bill={deleteBill} onClose={resetDeleteBill} />
        <BillErrorModal
          isOpen={isErrorInfoModalOpen}
          onClose={closeErrorInfoModal}
          bill={errorBill}
          onRetry={
            errorBill
              ? () => {
                  retryMutation.mutate(errorBill.id);
                  closeErrorInfoModal();
                }
              : undefined
          }
          isRetrying={retryMutation.isPending}
        />
        <BillInProgressModal
          isOpen={isInProgressInfoModalOpen}
          isUpdating={Boolean(inProgressBill?.externalId)}
          onClose={() => {
            closeInProgressInfoModal();
            setInProgressBill(null);
          }}
        />
      </div>
    </PageTemplate>
  );
};
